import React, { useState } from 'react'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { useDispatch, useSelector } from 'react-redux'

import { Button } from '../../../../components/UI'
import ErrorMessageWithExclamation from '../../../../components/UI/ErrorMessageWithExclamation'
import { Alert } from '../../../../components/UI'
import greenCheckmarkFlat from 'assets/icon/green-checkmark-flat.svg'

import { changePassword } from '../../../../routing/fetchingfunction/user/user.action'
import { selectLoadingChangePassword } from '../../../../routing/fetchingfunction/user/user.reducer'

import { ChangePasswordScheme } from '../../../../routing/utils/validators'

const ChangePassword = () => {
  const dispatch = useDispatch()
  const [showAlert, setShowAlert] = useState(false)
  const [alertMessage, setAlertMessage] = useState('')
  const [alertType, setAlertType] = useState('error')

  const isLoading = useSelector(selectLoadingChangePassword)

  const {
    formState: { errors },
    handleSubmit,
    register,
    reset,
    watch,
  } = useForm({
    defaultValues: {
      old_password: '',
      password: '',
      password2: '',
    },
    resolver: yupResolver(ChangePasswordScheme),
  })

  const handleFormSubmit = async (data) => {
    try {
      await dispatch(changePassword(data)).unwrap()
      reset()
      setAlertMessage('Password changed successfully!')
      setAlertType('success')
      setShowAlert(true)
    } catch (error) {
      setAlertMessage(`Error: ${error}`)
      setAlertType('error')
      setShowAlert(true)
    }
  }
  const password = watch('password')

  const validationItems = [
    { validation: 'hasMinLength', label: 'Min. 8 Characters', value: password?.length >= 8 },
    {
      validation: 'hasUppercase',
      label: 'At least one uppercase letter [A-Z]',
      value: /[A-Z]/.test(password || ''),
    },
    {
      validation: 'hasLowercase',
      label: 'At least one lowercase letter [a-z]',
      value: /[a-z]/.test(password || ''),
    },
    {
      validation: 'hasNumber',
      label: 'At least one number [0-9]',
      value: /[0-9]/.test(password || ''),
    },

    {
      validation: 'hasSpecialChar',
      label: 'At least one special character (e.g., @, #, $, %, etc.)',
      value: /[!@#$%^&*(),.?":{}|<>]/.test(password || ''),
    },
    {
      validation: 'hasNoSpace',
      label: 'No spaces allowed',
      value: /^(?=\S+$).+/.test(password || ''),
    }, // No spaces allowed
  ]

  return (
    <div className="mt-[24px] border-[1px] border-solid border-[#FFCA84] rounded-lg p-[20px]">
      <h5 className="text-sm font-semibold text-primary-100">Change My Password</h5>

      <h6 className="text-sm font-normal text-primary-100 my-[10px]">
        Your new password must be different from your previous password.
      </h6>

      <form
        className="flex flex-col gap-y-[20px] mt-2.5 border-[1px] border-solid border-[#FFCA84] rounded-lg p-[20px]"
        onSubmit={handleSubmit(handleFormSubmit)}
      >
        <div className="max-w-[320px] w-full">
          <label
            htmlFor="old_password"
            className="inline-block text-base font-semibold text-primary-100 mb-[6px]"
          >
            Current Password
          </label>

          <input
            id="old_password"
            name="old_password"
            type="password"
            placeholder="Type your current password"
            className="outline-none w-full rounded-[8px] bg-white shadow-button px-[14px] py-[10px] border-[#FFCA84] border-[1px] text-primary-100 text-base font-normal leading-[24px] placeholder:text-[#F6744380]"
            {...register('old_password')}
          />

          {errors.old_password && (
            <ErrorMessageWithExclamation text={errors.old_password.message} />
          )}
        </div>

        <div className="max-w-[320px] w-full">
          <label
            htmlFor="password"
            className="inline-block text-base font-semibold text-primary-100 mb-[6px]"
          >
            New Password
          </label>
          <input
            id="password"
            name="password"
            type="password"
            placeholder="Min. 8 characters"
            className="outline-none w-full rounded-[8px] bg-white shadow-button px-[14px] py-[10px] border-[#FFCA84] border-[1px] text-primary-100 text-base font-normal leading-[24px] placeholder:text-[#F6744380]"
            {...register('password')}
          />

          <div className="text-xs space-y-1 mt-1">
            {/* Requirement 1: Min 8 characters */}
            {validationItems.map((item, index) => (
              <div className={`flex items-center  'text-primary-75`}>
                <div className="w-5 mr-1 flex justify-center">
                  {item.value && (
                    <img
                      className="w-[13px] h-[13px] block"
                      src={greenCheckmarkFlat}
                      alt="Requirement met"
                    />
                  )}
                </div>
                <span className="text-xs text-warning-500">{item.label}</span>
              </div>
            ))}
          </div>

          {/* {errors.password && <ErrorMessageWithExclamation text={errors.password.message} />} */}
        </div>

        <div className="max-w-[320px] w-full">
          <label
            htmlFor="password2"
            className="inline-block text-base font-semibold text-primary-100 mb-[6px]"
          >
            Confirm New Password
          </label>

          <input
            id="password2"
            name="password2"
            type="password"
            placeholder="Min. 8 characters"
            className="outline-none w-full rounded-[8px] bg-white shadow-button px-[14px] py-[10px] border-[#FFCA84] border-[1px] text-primary-100 text-base font-normal leading-[24px] placeholder:text-[#F6744380]"
            {...register('password2')}
          />

          {errors.password2 && <ErrorMessageWithExclamation text={errors.password2.message} />}
        </div>

        <Button
          type="submit"
          className="w-fit min-w-[129px] text-sm font-semibold"
          isLoading={isLoading}
        >
          Save Changes
        </Button>

        {showAlert && (
          <Alert message={alertMessage} type={alertType} onClose={() => setShowAlert(false)} />
        )}
      </form>
    </div>
  )
}

export default ChangePassword
